import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DrawButton } from './DrawButton';
import { COOKIE_NAME } from './constants';
import * as styles from './styles.css';
import { trackAreaSelect } from './tracking';
import { setOnboardingCloseCookie } from './utils';
import { Desktop, NotDesktop } from '../../../../../../packages/Responsive';
import { closeOfferPreview } from '../../../../../common/actions/closeOfferPreview/closeOfferPreview';
import { fetchHybridSearchData } from '../../../../../common/actions/fetchHybridSearchData';
import { clearGeoType } from '../../../../../common/actions/jsonQuery';
import { setNonGeoParameters } from '../../../../../common/actions/nonGeoParameters';
import { OnboardingTooltip } from '../../../../../common/components/OnboardingTooltip';
import { preparePolygons } from '../../../../../common/mappers/polygons';
import { IApplicationState, TThunkDispatch } from '../../../../../common/types/redux';
import { finishDrawing, startDrawing } from '../../../../actions';
import { MapLayer } from '../../../../components/MapLayer';
import { selectIsDrawingMode } from '../../../../selectors';

export const DrawButtonContainer: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const isDrawingMode = useSelector(selectIsDrawingMode);
  const polygons = useSelector((state: IApplicationState) => preparePolygons(state.filters.jsonQuery));
  const [shouldShowOnboarding, setShouldShowOnboarding] = React.useState(false);
  const onboardingTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPolygons = polygons.length > 0;
  const isActive = (!hasPolygons && isDrawingMode) || shouldShowOnboarding;

  const hideOnboarding = React.useCallback(() => {
    setShouldShowOnboarding(false);
    setOnboardingCloseCookie();
  }, []);

  const showOnboarding = React.useCallback(() => {
    setShouldShowOnboarding(true);

    onboardingTimeoutRef.current = setTimeout(hideOnboarding, 5000);
  }, [hideOnboarding]);

  const handleActivateClick = React.useCallback(() => {
    hideOnboarding();
    dispatch(closeOfferPreview());

    if (!isDrawingMode) {
      dispatch(startDrawing());
      trackAreaSelect('start');
      dispatch(setNonGeoParameters(false));
    } else {
      dispatch(finishDrawing());
      trackAreaSelect('close');
    }
  }, [dispatch, hideOnboarding, isDrawingMode]);

  const handleCancelClick = React.useCallback(() => {
    hideOnboarding();
    dispatch(closeOfferPreview());

    dispatch(finishDrawing());
    dispatch(clearGeoType('polygon'));
    dispatch(fetchHybridSearchData({ deviceType }));
    trackAreaSelect('close');
  }, [deviceType, dispatch, hideOnboarding]);

  React.useEffect(() => {
    if (document.cookie.indexOf(COOKIE_NAME) === -1) {
      onboardingTimeoutRef.current = setTimeout(showOnboarding, 2000);
    }

    return () => {
      if (onboardingTimeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(onboardingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Desktop>
        <OnboardingTooltip
          tooltip="Выделяйте интересующую вас область на карте"
          open={shouldShowOnboarding}
          onClose={hideOnboarding}
        >
          <DrawButton
            active={isActive}
            hasPolygons={hasPolygons}
            onActivateClick={handleActivateClick}
            onCancelClick={handleCancelClick}
          />
        </OnboardingTooltip>
      </Desktop>
      <NotDesktop>
        <MapLayer className={styles['layer']}>
          <DrawButton
            active={isActive}
            hasPolygons={hasPolygons}
            onActivateClick={handleActivateClick}
            onCancelClick={handleCancelClick}
          />
        </MapLayer>
      </NotDesktop>
    </>
  );
};
